import { Chip, Input, Select, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { apiFetch } from '@/lib/api/client';
import type { ListResponse } from '@/lib/api/resource';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { useListParams } from '@/lib/list/useListParams';

import type { ActivityFilters, ActivityRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2's `/activity`, over docs/04-API-SPEC.md §6's
 * `GET /admin/activity-log ?subject_type&causer_id&from&to`.
 *
 * A read-only feed. There is no row action and no bulk anything: the value of
 * an audit trail is that nobody using it can change it, and a screen offering
 * a delete button would be answering a question this one exists to close.
 *
 * The diff is shown rather than summarised. "Updated the admissions page" is
 * what every other CMS says and is exactly the sentence that turns out not to
 * answer the question a year later; "requirements: … → …" does.
 */
export function ActivityPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams();

  const filters = useQuery({
    queryKey: ['admin', 'activity', 'filters'],
    queryFn: () => apiFetch<{ data: ActivityFilters }>('/admin/activity-log/filters'),
  });

  const rows = useQuery({
    queryKey: ['admin', 'activity', list.params],
    queryFn: () =>
      apiFetch<ListResponse<ActivityRow>>('/admin/activity-log', { query: list.params }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.activity')}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-secondary">{t('activity.intro')}</p>
      </div>

      {rows.isError ? <FormError message={t('errors.generic')} /> : null}

      <div className="flex flex-wrap items-end gap-3">
        <Select
          label={t('activity.filters.subject')}
          value={String(list.params.subject_type ?? '')}
          onChange={(event) => list.setFilter('subject_type', event.target.value || null)}
          options={[
            { value: '', label: t('activity.filters.allSubjects') },
            ...(filters.data?.data.subject_types ?? []).map((type) => ({
              value: type,
              label: t(`activity.subjects.${type}`, type),
            })),
          ]}
        />

        <Select
          label={t('activity.filters.event')}
          value={String(list.params.event ?? '')}
          onChange={(event) => list.setFilter('event', event.target.value || null)}
          options={[
            { value: '', label: t('activity.filters.allEvents') },
            ...(filters.data?.data.events ?? []).map((event) => ({
              value: event,
              label: t(`activity.events.${event}`, event),
            })),
          ]}
        />

        <Input
          type="date"
          label={t('activity.filters.from')}
          value={String(list.params.from ?? '')}
          onChange={(event) => list.setFilter('from', event.target.value || null)}
        />

        <Input
          type="date"
          label={t('activity.filters.to')}
          value={String(list.params.to ?? '')}
          onChange={(event) => list.setFilter('to', event.target.value || null)}
        />
      </div>

      {rows.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      <ol className="space-y-2">
        {(rows.data?.data ?? []).map((row) => (
          <li key={row.id} className="rounded-card border border-subtle p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              {/*
                Deliberately not a sentence.

                "ნინო კონტენტაძე შეცვალა სიახლე" is what the obvious template
                produces and it is wrong Georgian: the actor of a past-tense
                verb takes the ergative (`-მ`/`-მა`), whose form depends on the
                ending of a name the server cannot decline. Every CMS that
                interpolates a name into a localised sentence gets this wrong in
                Georgian and nobody notices because the developers do not read
                it (CLAUDE.md rule 1).

                So the action is a chip, the subject is a phrase, and the actor
                stands alone on the meta line — all three in the nominative,
                which needs no agreement with anything.
              */}
              <p className="flex flex-wrap items-baseline gap-2 text-body-sm text-primary">
                <Chip tone={row.event === 'deleted' ? 'gold' : 'neutral'}>
                  {t(`activity.events.${row.event ?? 'updated'}`, row.event ?? '')}
                </Chip>

                <span className="text-secondary">
                  {t(`activity.subjects.${row.subject.type ?? 'unknown'}`, row.subject.type ?? '')}
                </span>

                {row.subject.label ? (
                  <span className="font-semibold">{`„${row.subject.label}“`}</span>
                ) : (
                  // The record is gone; the row still says what was done to
                  // what type and by whom, which is the part that cannot be
                  // allowed to disappear with it.
                  <span className="text-muted">{t('activity.subjectGone')}</span>
                )}
              </p>

              <p className="text-caption text-muted">
                {/* Null when the row was written by a scheduled command or by a
                    migration — "the system did this", said plainly. */}
                {row.causer?.name ?? t('activity.system')}
                {row.created_at ? ` · ${new Date(row.created_at).toLocaleString(locale)}` : ''}
                {row.ip ? ` · ${row.ip}` : ''}
              </p>
            </div>

            <ChangeList changes={row.changes} event={row.event} />
          </li>
        ))}
      </ol>

      {rows.data?.data.length === 0 ? (
        <p className="rounded-card border border-subtle p-4 text-body-sm text-secondary">
          {t('activity.empty')}
        </p>
      ) : null}

      {rows.data && rows.data.meta.last_page > 1 ? (
        <nav aria-label={t('list.pagination')} className="flex flex-wrap items-center gap-2">
          <span className="text-caption text-muted">
            {t('list.pageOf', {
              page: rows.data.meta.current_page,
              total: rows.data.meta.last_page,
            })}
          </span>

          {rows.data.meta.current_page > 1 ? (
            <a
              href={list.buildPageHref(rows.data.meta.current_page - 1)}
              className="text-body-sm font-semibold text-navy-600 underline"
            >
              {t('list.previous')}
            </a>
          ) : null}

          {rows.data.meta.current_page < rows.data.meta.last_page ? (
            <a
              href={list.buildPageHref(rows.data.meta.current_page + 1)}
              className="text-body-sm font-semibold text-navy-600 underline"
            >
              {t('list.next')}
            </a>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * spatie's `{attributes, old}` pair, as "field: before → after".
 *
 * Values are stringified rather than rendered: a translatable field arrives as
 * a per-locale object and a status as an enum, and an audit trail that only
 * knows how to display strings is better than one that renders `[object
 * Object]` for the half of the columns that matter most here.
 *
 * **A creation is folded away.** `logOnlyDirty` treats every column of a new
 * record as changed, so a created row carries thirty fields and the feed
 * becomes one record per screen — which is the same as having no feed. An
 * update carries only what moved, and that is the case anyone opens this
 * screen for, so it is the one shown open. Nothing is hidden either way: the
 * `<details>` holds the whole payload, because an audit trail that summarises
 * away the evidence is not one.
 */
function ChangeList({ changes, event }: { changes: ActivityRow['changes']; event: string | null }) {
  const { t } = useTranslation();

  const fields = Object.keys(changes.attributes);

  if (fields.length === 0) {
    return null;
  }

  const list = (
    <dl className="space-y-1">
      {fields.map((field) => (
        <div key={field} className="flex flex-wrap items-baseline gap-2 text-caption">
          <dt className="font-semibold text-secondary">{field}</dt>
          <dd className="flex min-w-0 flex-wrap items-baseline gap-1.5 text-muted">
            {field in changes.old ? (
              <>
                <span className="line-through">{stringify(changes.old[field])}</span>
                <span aria-hidden="true">{'→'}</span>
              </>
            ) : null}
            <span className="text-primary">{stringify(changes.attributes[field])}</span>
          </dd>
        </div>
      ))}
    </dl>
  );

  if (event === 'updated') {
    return <div className="mt-3 border-t border-subtle pt-3">{list}</div>;
  }

  return (
    <details className="mt-3 border-t border-subtle pt-3">
      <summary className="cursor-pointer text-caption text-muted">
        {t('activity.fieldCount', { count: fields.length })}
      </summary>
      <div className="mt-2">{list}</div>
    </details>
  );
}

/**
 * One line per value, truncated.
 *
 * A `body` column holds an entire page of HTML; printed in full it buries every
 * other row on the screen. The full value is a query away in the record itself
 * — what this list is for is telling you *which* field moved.
 */
function stringify(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const text =
    typeof value === 'object'
      ? Object.entries(value as Record<string, unknown>)
          .filter(([, entry]) => entry !== null && entry !== '')
          .map(([key, entry]) =>
            Array.isArray(value) ? String(entry) : `${key}: ${String(entry)}`,
          )
          .join(' · ')
      : String(value);

  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}
